import { useGsap } from "@/hooks/useGsap";
import { gsap, ScrollTrigger, EASE, prefersReducedMotion } from "@/lib/motion";
import { WRAP } from "@/components/story/layout";

/**
 * Moment 2: the problem, stated once, in type.
 *
 * No illustration. Three words sit slightly out of line when the block enters
 * the viewport and then pull into the sentence: the sentence performs its own
 * argument. Everything is rendered in its final position in the markup, so a
 * reduced-motion visitor or a failed script still reads a clean paragraph.
 */

/* Each fragment is a word or phrase. `drift` is the x/y it starts from. */
const LINE_ONE = [
  { t: "Most automation does not fail" },
  { t: "because the idea was wrong." },
];

const LINE_TWO = [
  { t: "It fails because nobody ever wrote down" },
  { t: "how", drift: null },
  { t: "the", drift: null },
  { t: "work", drift: [-18, 10] },
  { t: "actually", drift: [14, -12] },
  { t: "moves.", drift: [22, 14] },
];

export default function ProblemScene() {
  const scope = useGsap((ctx, el) => {
    if (prefersReducedMotion()) return;

    const words = el.querySelectorAll("[data-word]");
    const drifters = el.querySelectorAll("[data-drift]");
    const label = el.querySelector("[data-label]");

    const play = () => {
      const tl = gsap.timeline({ defaults: { ease: EASE } });
      tl.fromTo(label, { opacity: 0 }, { opacity: 1, duration: 0.4 });
      tl.fromTo(
        words,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.05 },
        "-=0.2"
      );
      /* The three loose words arrive off their line, then settle onto it. */
      tl.fromTo(
        drifters,
        {
          x: (i) => Number(drifters[i].dataset.dx || 0),
          y: (i) => Number(drifters[i].dataset.dy || 0),
        },
        { x: 0, y: 0, duration: 0.85, stagger: 0.07 },
        "-=0.45"
      );
    };

    try {
      ScrollTrigger.create({ trigger: el, start: "top 78%", once: true, onEnter: play });
    } catch {
      // Motion is progressive enhancement; the sentence must stay readable.
    }
  }, []);

  return (
    <section ref={scope} className="bg-ink text-paper border-b border-ink" data-testid="section-problem">
      <div className={`${WRAP} story-pad`}>
        <p data-label className="story-index story-index--paper">01 / The problem</p>

        <h2 className="display story-h2 mt-6 max-w-[30ch]">
          {LINE_ONE.map((w) => (
            <span key={w.t} data-word className="block">
              {w.t}
            </span>
          ))}
        </h2>

        <p className="display story-h2 mt-7 max-w-[32ch] text-paper/70">
          {LINE_TWO.map((w, i) =>
            w.drift ? (
              <span
                key={`${w.t}-${i}`}
                data-word
                data-drift
                data-dx={w.drift[0]}
                data-dy={w.drift[1]}
                className="inline-block"
              >
                {w.t}&nbsp;
              </span>
            ) : (
              <span key={`${w.t}-${i}`} data-word className="inline-block">
                {w.t}&nbsp;
              </span>
            )
          )}
        </p>

        <p data-word className="mt-10 max-w-[46ch] story-body text-paper/60">
          Owners know their business. What is missing is a written picture of how a job
          travels from the first phone call to the money in the bank. Until that exists,
          every tool bought to fix it becomes one more place to check.
        </p>
      </div>
    </section>
  );
}
